// Lead Scoring Service
// Connects to backend lead scoring API

const API_BASE = process.env.NODE_ENV === 'production' 
  ? 'https://api.explanatum.co.uk'
  : 'http://localhost:3000';

export interface LeadScore {
  score: number;
  percentage: number;
  grade: string;
  calculated_at: string;
}

export interface ScoringRule {
  id: string;
  name: string;
  entity_type: string;
  field: string;
  operator: string;
  value: any;
  points: number;
  weight: number;
  is_active: boolean;
  matched?: boolean;
}

export interface ScoreHistory {
  id: string;
  score: number;
  percentage: number;
  grade: string;
  calculated_at: string;
  trigger_type: string;
}

const leadScoringService = {
  /**
   * Get current lead score for an entity
   */
  async getScore(
    organizationId: string,
    entityType: 'contact' | 'company' | 'deal',
    entityId: string
  ): Promise<LeadScore | null> {
    const response = await fetch(
      `${API_BASE}/api/crm/lead-scoring/score?organization_id=${organizationId}&entity_type=${entityType}&entity_id=${entityId}`,
      {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      }
    );

    if (!response.ok) {
      if (response.status === 404) return null;
      throw new Error('Failed to fetch lead score');
    }

    const { data } = await response.json();
    return data;
  },

  /**
   * Calculate score for an entity
   */
  async calculateScore(
    organizationId: string,
    entityType: 'contact' | 'company' | 'deal',
    entityId: string
  ): Promise<LeadScore> {
    const response = await fetch(`${API_BASE}/api/crm/lead-scoring/calculate`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        organization_id: organizationId,
        entity_type: entityType,
        entity_id: entityId
      })
    });

    if (!response.ok) {
      throw new Error('Failed to calculate lead score');
    }

    const { data } = await response.json();
    return data;
  },

  /**
   * Get score history
   */
  async getScoreHistory(
    organizationId: string,
    entityType: 'contact' | 'company' | 'deal',
    entityId: string,
    limit: number = 10
  ): Promise<ScoreHistory[]> {
    const response = await fetch(
      `${API_BASE}/api/crm/lead-scoring/history?organization_id=${organizationId}&entity_type=${entityType}&entity_id=${entityId}&limit=${limit}`,
      {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch score history');
    }

    const { data } = await response.json();
    return data || [];
  },

  /**
   * Get scoring rules
   */
  async getRules(organizationId: string, entityType?: string): Promise<ScoringRule[]> {
    let url = `${API_BASE}/api/crm/lead-scoring/rules?organization_id=${organizationId}`;
    if (entityType) {
      url += `&entity_type=${entityType}`;
    }

    const response = await fetch(url, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch scoring rules');
    }

    const { data } = await response.json();
    return data || [];
  },

  /**
   * Get score breakdown showing which rules matched
   */
  async getScoreBreakdown(
    organizationId: string,
    entityType: 'contact' | 'company' | 'deal',
    entityId: string
  ): Promise<ScoringRule[]> {
    const rules = await this.getRules(organizationId, entityType);
    
    // Note: In a real implementation, the backend should return which rules matched
    // For now, we return all rules and let the UI determine matches
    return rules;
  },

  /**
   * Initialize default scoring rules for an organization
   */
  async initializeDefaultRules(organizationId: string, userId: string): Promise<void> {
    const response = await fetch(`${API_BASE}/api/crm/lead-scoring/initialize`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        organization_id: organizationId,
        userId
      })
    });

    if (!response.ok) {
      throw new Error('Failed to initialize scoring rules');
    }
  }
};

export default leadScoringService;
